import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit, Trash2, Search, Copy, ExternalLink, Rocket, ListChecks } from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';

interface LandingPageItem {
  _id: string;
  internalName: string;
  slug: string;
  status: 'draft' | 'published';
  avatar?: { name: string; slug: string } | null;
  views: number;
  createdAt: string;
}

const AdminLandingPages: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [landingPages, setLandingPages] = useState<LandingPageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const fetchLandingPages = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '50',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus })
      });
      const response = await axios.get(`/landing-pages?${params}`);
      setLandingPages(response.data.landingPages);
    } catch (error) {
      console.error('Erro ao carregar landing pages:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedStatus]);

  useEffect(() => {
    fetchLandingPages();
  }, [fetchLandingPages]);

  const handleDelete = async (lp: LandingPageItem) => {
    if (!(await confirm({ title: 'Excluir landing page', message: <>A landing page <strong className="text-gray-900">{lp.internalName}</strong> será excluída permanentemente e o link público deixará de funcionar.</>, confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/landing-pages/${lp._id}`);
      setLandingPages((prev) => prev.filter((item) => item._id !== lp._id));
      notifications.showSuccess('Landing page deletada!', 'A página foi removida com sucesso.');
    } catch (error: any) {
      console.error('Erro ao deletar landing page:', error);
      notifications.showError('Erro ao deletar', error.response?.data?.message || 'Tente novamente.');
    }
  };

  const publicUrl = (slug: string) => `${window.location.origin}/l/${slug}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.landingPages')}</h1>
              <p className="text-gray-600">{t('admin.landingPagesDesc')}</p>
            </div>
            <div className="flex flex-wrap gap-2 self-start">
              <Link to="/admin/landing-pages/leads" className="btn-outline inline-flex items-center space-x-2">
                <ListChecks className="w-5 h-5" />
                <span>{t('admin.quizLeads')}</span>
              </Link>
              <Link to="/admin/landing-pages/new" className="btn-primary inline-flex items-center space-x-2">
                <Plus className="w-5 h-5" />
                <span>{t('admin.landingPageNew')}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('common.search') as string}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="lg:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="draft">Rascunho</option>
                <option value="published">Publicada</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : landingPages.length === 0 ? (
            <div className="p-8 text-center">
              <Rocket className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma landing page criada</h3>
              <Link to="/admin/landing-pages/new" className="btn-primary">
                {t('admin.landingPageNew')}
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campanha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avatar</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {landingPages.map((lp) => (
                    <tr key={lp._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{lp.internalName}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1.5">
                          /l/{lp.slug}
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(publicUrl(lp.slug)); notifications.showSuccess('Copiado!', 'Link copiado.'); }}
                            title="Copiar link"
                          >
                            <Copy className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700" />
                          </button>
                          {lp.status === 'published' && (
                            <a href={publicUrl(lp.slug)} target="_blank" rel="noopener noreferrer" title="Abrir">
                              <ExternalLink className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${lp.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {lp.status === 'published' ? 'Publicada' : 'Rascunho'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lp.avatar?.name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lp.views}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/admin/landing-pages/${lp._id}/edit`}
                            className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors duration-200"
                            title={t('admin.edit')}
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDelete(lp)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLandingPages;
